import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StarInput } from "@/components/StarRating";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const TAGS = ["Prompt & Punctual", "Exact Item Condition", "Polite Communication", "Fair Bargain", "Smooth Handoff"];

export default function RatingModal({ open, onOpenChange, transaction, onRated }) {
  const [stars, setStars] = useState(5);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleTag = (t) => setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/transactions/${transaction.id}/rate`, { stars, tags, comment });
      toast.success("Rating submitted. Thanks for building campus trust!");
      onOpenChange(false);
      setStars(5); setTags([]); setComment("");
      onRated && onRated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not submit rating"));
    } finally {
      setLoading(false);
    }
  };

  if (!transaction) return null;
  const cp = transaction.counterparty;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="rating-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Rate {transaction.my_role === "buyer" ? "your seller" : "your buyer"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <img src={cp?.avatar} alt={cp?.name} className="h-11 w-11 rounded-full border border-slate-200" />
          <div>
            <div className="font-semibold text-ink">{cp?.name}</div>
            <div className="text-xs text-slate-500">{transaction.listing?.title}</div>
          </div>
        </div>

        <div className="mt-2 flex flex-col items-center gap-2 py-3">
          <StarInput value={stars} onChange={setStars} />
          <span className="text-sm text-slate-500">{stars} out of 5 stars</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => (
            <button
              key={t}
              onClick={() => toggleTag(t)}
              data-testid={`rating-tag-${t}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                tags.includes(t) ? "bg-ink text-white border-ink" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <Textarea
          value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Add a note (optional)" className="mt-1" data-testid="rating-comment"
        />

        <Button onClick={submit} disabled={loading} className="w-full bg-brand hover:bg-brand-700 text-white h-11" data-testid="rating-submit-btn">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit rating"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
